import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  AlertTriangle,
  BadgeCheck,
  Phone,
  PhoneOff,
  ShieldCheck,
  UserRound,
  X,
} from 'lucide-react';
import { formatLongDate } from '@unifemga/shared';
import { ApiError, api } from '@/lib/api';
import { cn, money } from '@/lib/utils';
import {
  Alert,
  Button,
  Field,
  Input,
  Modal,
  ModalHeader,
  Select,
  Skeleton,
  Textarea,
} from '@/components/ui';
import { StatusBadge } from '@/components/StatusBadge';
import { GUARANTOR_STATUS } from '@/lib/status';

interface Guarantor {
  id: string;
  fullName: string;
  relationship: string;
  phone: string;
  alternatePhone: string | null;
  email: string | null;
  address: string;
  occupation: string | null;
  employerName: string | null;
  membershipNumber: string | null;
  status: string;
  confirmedBy: string | null;
  confirmedAt: string | null;
  contactedOn: string | null;
  callNotes: string | null;
  declineReason: string | null;
  isRegisteredMember: boolean;
  memberOutstanding: string | null;
  memberInArrears: boolean;
}

/**
 * Guarantor confirmation calls.
 *
 * The borrower named these people on the form having already spoken to them.
 * An officer telephones each one; that call is the entire evidentiary basis of
 * the guarantee, so who called, the number reached and what was said are all
 * recorded here.
 */
export function GuarantorCalls({
  applicationId,
  requiredCount,
  onChange,
}: {
  applicationId: string;
  requiredCount: number;
  onChange?: () => void;
}) {
  const queryClient = useQueryClient();
  const [calling, setCalling] = useState<Guarantor | null>(null);

  const { data: guarantors = [], isLoading } = useQuery({
    queryKey: ['application-guarantors', applicationId],
    queryFn: () => api.get<Guarantor[]>(`/guarantors/application/${applicationId}`),
  });

  if (isLoading) return <Skeleton className="h-40" />;

  const confirmed = guarantors.filter((g) => g.status === 'CONFIRMED').length;
  const ready = confirmed >= requiredCount;

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ShieldCheck size={15} className="text-foreground-secondary" />
          <span className="text-sm font-semibold text-foreground">Guarantors</span>
          <span
            className={cn(
              'numeric rounded-full px-2 py-0.5 text-xs font-bold',
              ready
                ? 'bg-success-soft text-success-soft-foreground'
                : 'bg-warning-soft text-warning-soft-foreground',
            )}
          >
            {confirmed}/{requiredCount} confirmed
          </span>
        </div>
      </div>

      {!ready && (
        <Alert tone="warning" className="mb-3">
          <span className="inline-flex items-start gap-1.5">
            <Phone size={14} className="mt-0.5 shrink-0" />
            Telephone each guarantor and record the call. Approval is blocked until{' '}
            {requiredCount} {requiredCount === 1 ? 'is' : 'are'} confirmed.
          </span>
        </Alert>
      )}

      <div className="space-y-2">
        {guarantors.length === 0 && (
          <p className="text-sm text-foreground-secondary">
            No guarantors were named on this application.
          </p>
        )}

        {guarantors.map((guarantor) => (
          <div
            key={guarantor.id}
            className={cn(
              'rounded-xl border p-4',
              guarantor.status === 'CONFIRMED'
                ? 'border-success/25 bg-success-soft/30'
                : guarantor.status === 'DECLINED'
                  ? 'border-danger/25 bg-danger-soft/30'
                  : 'border-border',
            )}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-semibold text-foreground">{guarantor.fullName}</p>
                  <StatusBadge map={GUARANTOR_STATUS} status={guarantor.status} />
                </div>

                <p className="mt-0.5 text-sm text-foreground-secondary">
                  {guarantor.relationship}
                  {guarantor.occupation ? ` · ${guarantor.occupation}` : ''}
                  {guarantor.employerName ? ` at ${guarantor.employerName}` : ''}
                </p>

                <div className="mt-2 space-y-0.5 text-sm">
                  <p className="numeric flex items-center gap-1.5 font-semibold text-foreground">
                    <Phone size={12} /> {guarantor.phone}
                    {guarantor.alternatePhone && (
                      <span className="font-normal text-foreground-secondary">
                        · {guarantor.alternatePhone}
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-foreground-secondary">{guarantor.address}</p>
                  {guarantor.email && (
                    <p className="text-xs text-foreground-secondary">{guarantor.email}</p>
                  )}
                </div>

                {/* If they happen to be a member, a reviewer wants to know what
                    they already owe before relying on them again. */}
                {guarantor.isRegisteredMember && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1 rounded-full bg-info-soft px-2 py-0.5 text-[11px] font-semibold text-info-soft-foreground">
                      <UserRound size={10} /> UNIFEMGA member
                      {guarantor.membershipNumber ? ` · ${guarantor.membershipNumber}` : ''}
                    </span>
                    {guarantor.memberOutstanding && Number(guarantor.memberOutstanding) > 0 && (
                      <span
                        className={cn(
                          'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold',
                          guarantor.memberInArrears
                            ? 'bg-danger-soft text-danger-soft-foreground'
                            : 'bg-neutral-soft text-neutral-soft-foreground',
                        )}
                      >
                        {guarantor.memberInArrears && <AlertTriangle size={10} />}
                        Owes {money(guarantor.memberOutstanding)}
                        {guarantor.memberInArrears ? ' · in arrears' : ''}
                      </span>
                    )}
                  </div>
                )}

                {guarantor.confirmedAt && (
                  <p className="mt-2 text-xs text-foreground-secondary">
                    {guarantor.status === 'CONFIRMED' ? 'Confirmed' : 'Called'} by{' '}
                    <strong className="text-foreground">{guarantor.confirmedBy}</strong> on{' '}
                    {formatLongDate(new Date(guarantor.confirmedAt))}
                    {guarantor.contactedOn ? ` — reached on ${guarantor.contactedOn}` : ''}
                  </p>
                )}
                {guarantor.callNotes && (
                  <p className="mt-1 rounded-lg bg-background px-3 py-2 text-xs italic text-foreground-secondary">
                    &ldquo;{guarantor.callNotes}&rdquo;
                  </p>
                )}
                {guarantor.declineReason && (
                  <p className="mt-1 text-xs font-medium text-danger">
                    Declined: {guarantor.declineReason}
                  </p>
                )}
              </div>

              <Button
                size="sm"
                variant={guarantor.status === 'CONFIRMED' ? 'ghost' : 'outline'}
                onClick={() => setCalling(guarantor)}
              >
                <Phone size={14} />
                {guarantor.status === 'PENDING_CONFIRMATION' ? 'Record call' : 'Update'}
              </Button>
            </div>
          </div>
        ))}
      </div>

      <CallPanel
        guarantor={calling}
        onClose={() => setCalling(null)}
        onDone={() => {
          setCalling(null);
          void queryClient.invalidateQueries({ queryKey: ['application-guarantors', applicationId] });
          void queryClient.invalidateQueries({ queryKey: ['applications'] });
          onChange?.();
        }}
      />
    </div>
  );
}

function CallPanel({
  guarantor,
  onClose,
  onDone,
}: {
  guarantor: Guarantor | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [outcome, setOutcome] = useState<'CONFIRMED' | 'UNREACHABLE' | 'DECLINED'>('CONFIRMED');
  const [contactedOn, setContactedOn] = useState('');
  const [callNotes, setCallNotes] = useState('');
  const [declineReason, setDeclineReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const record = useMutation({
    mutationFn: () =>
      api.post<{ message: string }>('/guarantors/confirm', {
        guarantorId: guarantor!.id,
        outcome,
        contactedOn: contactedOn || undefined,
        callNotes: callNotes || undefined,
        declineReason: declineReason || undefined,
      }),
    onSuccess: (result) => {
      toast.success(result.message);
      setContactedOn('');
      setCallNotes('');
      setDeclineReason('');
      setError(null);
      onDone();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Something went wrong.'),
  });

  return (
    <Modal open={guarantor !== null} onClose={onClose}>
      {guarantor && (
        <>
          <ModalHeader
            title={`Call ${guarantor.fullName}`}
            description={`${guarantor.relationship} · ${guarantor.phone}`}
            onClose={onClose}
          />

          <div className="space-y-5 p-6">
            <div className="rounded-xl border border-border bg-muted p-4">
              <p className="mb-2 text-xs font-bold uppercase tracking-wider text-foreground-secondary">
                Numbers to try
              </p>
              <div className="space-y-1">
                <a
                  href={`tel:${guarantor.phone}`}
                  className="numeric flex items-center gap-2 font-semibold text-primary hover:underline"
                >
                  <Phone size={13} /> {guarantor.phone}
                </a>
                {guarantor.alternatePhone && (
                  <a
                    href={`tel:${guarantor.alternatePhone}`}
                    className="numeric flex items-center gap-2 font-semibold text-primary hover:underline"
                  >
                    <Phone size={13} /> {guarantor.alternatePhone}
                  </a>
                )}
              </div>
            </div>

            <Alert tone="info" title="What to establish on the call">
              That they know the borrower, agree to guarantee the loan, and understand that if it
              is not repaid UNIFEMGA may seek the outstanding <strong>principal</strong> from them
              — never more, because no interest or penalty is ever added.
            </Alert>

            {error && <Alert tone="danger">{error}</Alert>}

            <Field label="Outcome" htmlFor="outcome" required>
              <Select
                id="outcome"
                value={outcome}
                onChange={(e) => setOutcome(e.target.value as typeof outcome)}
              >
                <option value="CONFIRMED">Confirmed — they agreed</option>
                <option value="UNREACHABLE">Could not reach them</option>
                <option value="DECLINED">They declined</option>
              </Select>
            </Field>

            {outcome === 'CONFIRMED' && (
              <Field
                label="Number you reached them on"
                htmlFor="contactedOn"
                required
                hint="Recorded as evidence that the call happened."
              >
                <Input
                  id="contactedOn"
                  className="numeric"
                  value={contactedOn}
                  onChange={(e) => setContactedOn(e.target.value)}
                  placeholder={guarantor.phone}
                />
              </Field>
            )}

            {outcome === 'DECLINED' && (
              <Field label="Why did they decline?" htmlFor="declineReason" required>
                <Textarea
                  id="declineReason"
                  rows={2}
                  value={declineReason}
                  onChange={(e) => setDeclineReason(e.target.value)}
                />
              </Field>
            )}

            <Field
              label="Call notes"
              htmlFor="callNotes"
              hint="Anything worth recording about the conversation."
            >
              <Textarea
                id="callNotes"
                rows={3}
                value={callNotes}
                onChange={(e) => setCallNotes(e.target.value)}
                placeholder="e.g. Spoke to him directly; he confirmed he knows the applicant and agrees."
              />
            </Field>

            <div className="flex gap-3 border-t border-border pt-5">
              <Button
                full
                variant={outcome === 'DECLINED' ? 'danger' : 'primary'}
                loading={record.isPending}
                onClick={() => record.mutate()}
              >
                {outcome === 'CONFIRMED' ? (
                  <>
                    <BadgeCheck size={16} /> Record confirmation
                  </>
                ) : outcome === 'UNREACHABLE' ? (
                  <>
                    <PhoneOff size={16} /> Record no answer
                  </>
                ) : (
                  <>
                    <X size={16} /> Record decline
                  </>
                )}
              </Button>
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}
